/**
 * AssetsContext - Provides recipe, food, and other asset data
 * 
 * This context provides read-only access to content assets that are
 * loaded once and rarely change, preventing re-renders from other updates.
 */

import React, { createContext, useContext } from 'react';
import PropTypes from 'prop-types';

// Create the context
const AssetsContext = createContext({
    recipes: {},
    foods: {},
    details: {},
    brands: {},
    merchants: {},
});

// Custom hook for consuming assets
export const useAssets = () => {
    const context = useContext(AssetsContext);
    if (!context) {
        throw new Error('useAssets must be used within AssetsProvider');
    }
    return context;
};

// Provider component
export const AssetsProvider = ({ 
    children, 
    assets = {} 
}) => {
    // Assets are stable references, no need for useMemo
    const value = {
        recipes: assets.recipes || {},
        foods: assets.foods || {},
        details: assets.details || {},
        brands: assets.brands || {},
        merchants: assets.merchants || {},
    };
    
    return (
        <AssetsContext.Provider value={value}>
            {children}
        </AssetsContext.Provider>
    );
};

AssetsProvider.propTypes = {
    children: PropTypes.node.isRequired,
    assets: PropTypes.shape({
        recipes: PropTypes.object,
        foods: PropTypes.object,
        details: PropTypes.object,
        brands: PropTypes.object,
        merchants: PropTypes.object,
    }),
};

export default AssetsContext;